// Tiny fetch wrapper. Throws on non-2xx. JSON in, JSON out.
// In DEMO_MODE every request is short-circuited with canned data so the site
// works without the backend.

import { DEMO_MODE, demoQueue, demoHolds, demoImported, demoReportsSummary, demoRxDetail } from "./demoData";

function delay<T>(value: T, ms = 250): Promise<T> {
  return new Promise((res) => setTimeout(() => res(value), ms));
}

function mockGet<T>(path: string): Promise<T> | null {
  // /queue, /queue?store=XX, /queue?store=XX&filter=all&limit=200
  if (path.startsWith("/queue")) return delay(demoQueue as unknown as T);
  if (path.startsWith("/holds")) return delay(demoHolds as unknown as T);
  if (path.startsWith("/imported")) return delay(demoImported as unknown as T);
  if (path.startsWith("/reports/summary")) return delay(demoReportsSummary as unknown as T);
  if (path.startsWith("/rx/")) {
    // /rx/123 or /rx/123/formula-candidates
    if (path.includes("/formula-candidates")) return delay([] as unknown as T);
    const idMatch = path.match(/^\/rx\/(\d+)/);
    const id = idMatch ? Number(idMatch[1]) : 1042;
    return delay(demoRxDetail(id) as unknown as T);
  }
  if (path.startsWith("/patients/search") || path.startsWith("/prescribers/search")) {
    return delay([] as unknown as T);
  }
  if (path.startsWith("/stores")) {
    return delay([
      { code: "ER", name: "Erie" },
      { code: "HST", name: "Houston" },
      { code: "GV", name: "Greenville" },
    ] as unknown as T);
  }
  if (path.startsWith("/health")) return delay({ ok: true } as unknown as T);
  return null;
}

function mockPost<T>(path: string, body: unknown): Promise<T> | null {
  if (path === "/intakes/upload") {
    // Simulate AI extraction time (~3s) then return a fake rxId so the UI
    // navigates to /review/<id>.
    const rxId = 1042;
    return new Promise((res) => setTimeout(() => res({ rxIds: [rxId], intakeId: 555, extractionId: 777, rxCount: 1, overallConfidence: 0.92, durationMs: 2900 } as unknown as T), 2800));
  }
  // Generic happy-path return
  return delay({ ok: true, body } as unknown as T);
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  if (DEMO_MODE) {
    const mocked = mockGet<T>(path);
    if (mocked) return mocked;
  }
  const res = await fetch(`/api${path}`, { signal });
  if (!res.ok) throw new Error(`GET ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

export async function apiPost<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  if (DEMO_MODE) {
    const mocked = mockPost<T>(path, body);
    if (mocked) return mocked;
  }
  const res = await fetch(`/api${path}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`POST ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

export async function apiPatch<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  if (DEMO_MODE) return delay({ ok: true, body } as unknown as T);
  const res = await fetch(`/api${path}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) throw new Error(`PATCH ${path} → ${res.status} ${await res.text().catch(() => "")}`);
  return res.json() as Promise<T>;
}
